/**
 * API de diffusion de messages selon une approche Message-oriented middleware (MoM)
 * https://en.wikipedia.org/wiki/Message-oriented_middleware
 *
 */

/** Recepteur des messages. */
export interface IInfoConsumer {

	/**
	 * Signature de cet IInfoConsumer. Peut permettre à un IInfoBroker de décider des stratégies de
	 * diffusion, ordonnancement, transformations (...) des infos pour chaque IInfoConsumer abonné.
	 */
	infoHolderSgn?: string

	/** Réception d'une info envoyée (en principe) par un IInfoBroker. */
	onInfo(info: IInfo): void
}

/** Producteur d'une info. */
export type IInfoProducer = any;

/** Information circulante. */
export interface IInfo {
	/** Chaine des IInfoProducer / IInfoNodes par lesquels ce msg est déjà passé en commencant par le producteur initial de l'info. */
	infoHolders?: IInfoProducer[]
}


/** Message requête pour chercher un objet particulier. */
export class InfoReqObject<T> implements IInfo {
	/** Signature de l'objet recherché. */
	sgnObject: string;

	/** Objet retourné.*/
	objectFound?: T;

	constructor(sgnObject: string) {
		this.sgnObject = sgnObject;
	}

	infoHolders?: IInfoProducer[];
}

// /**
//  * Info générique indiquant que l'ihm devrait être gelée (en édition en particulier)
//  * en raison d'une interruption ou indisponibilité temporaire du serveur.
//  * Des sous-classes peuvent préciser les raisons, le scope, etc.
//  * @see InfoUnfreezeUi
//  */
// export class InfoFreezeUi implements IInfo {
// }
//
// /**
//  * Info générique indiquant que l'ihm peut-être dégelée.
//  * @see InfoFreezeUi
//  */
// export class InfoUnfreezeUi implements IInfo {
// }

/** Réceptionne des infos de différents IInfoProducer et les retransmet selon ses propres règles aux IInfoConsumer abonnés. */
export interface IInfoBroker {

	addConsumer(consumer: IInfoConsumer): void

	removeConsumer(consumer: IInfoConsumer): void

	/**
	 * Diffuse un message en indiquant sa provenance 'from'.
	 * C'est à l'implémentation IInfoBroker d'ajouter le IInfoConsumer 'from' à la pile IInfo.infoHolders
	 * Usage type:
	 *   this.infoBroker.dispatchInfo({infoType:'test'}, this);
	 */
	dispatchInfo(info: IInfo, from: IInfoProducer): void
}

/**
 * Chaque IInfoNode est un IInfoBroker qui retransmet les infos en tant que IInfoProducer
 * à d'autres IInfoBroker ou IInfoNode. Des arbres ou des graphes de diffusion d'infos
 * sont ainsi constitués.
 *
 * Avant retransmission d'une info, ce IInfoNode s'ajoute au tableau IInfo.infoHolders.
 * Chaque IInfoNode doit implémenter un contôle de cycle infini fondé sur la pile IInfo.infoHolders.
 */
interface IInfoNode extends IInfoBroker, IInfoProducer {
}

export interface IInfoBrokerPointer {
	infoBroker?: IInfoBroker
}

export function isInfoBrokerPointer(ibp:any): ibp is IInfoBrokerPointer {return ibp?.infoBroker != null}

/**
 * Implémenattion la plus basique qui soit.
 *
 * Au fur et à mesure des besoins, d'autres implémentations plus ou moins spécifiques sont
 * possibles avec de mécasnimes de :
 * - Relation parent-enfant entre IInfoBroker pour gérer un arbre de dispatching. Un InfoBrokerChild,
 *   de type IInfoNode, peut filtre et transmet certaines infos à son IInfoBroker parent.
 * - Filtrage via le type du msg et le infoHolderSgn des infos.
 * - Ordonancement / priorisation des IInfoConsumer pour l'envoi des info.
 * - Transformation de l'info avant transnmission à certains IInfoConsumer.
 * - ...
 *
 */
export class InfoBrokerBasic implements IInfoBroker {

	protected consumers = new Set<IInfoConsumer>();

	addConsumer(consumer: IInfoConsumer) {
		this.consumers.add(consumer);
	}

	removeConsumer(consumer: IInfoConsumer) {
		this.consumers.delete(consumer);
	}

	dispatchInfo(info: IInfo, from: IInfoProducer) {
		if (info.infoHolders) info.infoHolders.push(from);
		else info.infoHolders = [from];
		for (const c of this.consumers) {
			if (c !== from) try {c.onInfo(info)} catch (e) {console.error(e)}
		}
	}

	removeAllConsumers() {
		this.consumers.clear();
	}

	protected dispatchInfoFiltered(info: IInfo, from: IInfoProducer, filter: (c: IInfoConsumer) => boolean) {
		if (info.infoHolders) info.infoHolders.push(from);
		else info.infoHolders = [from];
		for (const c of this.consumers) {
			if (c !== from && filter(c)) try {c.onInfo(info)} catch (e) {console.error(e)}
		}
	}
}
